/**
 * `aethereum handoff [session-id]` / `aethereum resume-handoff [artifact-id]` —
 * the CLI surface for v4-S10 E2EE session handoff (the marquee).
 *
 * Encryption happens HERE, on your machine, via the existing code-share
 * primitives; the hosted rail stores and serves only ciphertext it cannot read.
 * Kept deliberately thin (the code.ts pattern): all the security lives in
 * `handoff.ts` / `e2ee.ts` / `hosted-handoff-client.ts`.
 *
 * Resume placement: the transcript lands under the CURRENT machine's resolved
 * Claude config dir at projects/<encoded absolute CURRENT cwd>/<sessionId>.jsonl.
 * The project-dir encoding is RECOMPUTED from the local cwd — the source
 * machine's directory name is never trusted or reused. Codex artifacts land
 * under ~/.codex/sessions/YYYY/MM/DD/ as a rollout file instead.
 */

import { execFileSync } from "node:child_process";
import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { join, resolve } from "node:path";
import { homedir } from "node:os";
import { scanForInjection, injectionWarning } from "@br9704/aethereum-shared";
import { resolveProject } from "../hive/config-store.js";
import { HostedHandoffClient } from "../hive/hosted-handoff-client.js";
import { HandoffTooLargeError, type HandoffPayload } from "../hive/handoff.js";
import { readIndex, readGitBranch, claudeConfigDir, codexSessionsDir } from "../sessions/index-store.js";
import { makeFirstContactGate, type ShareConsentDeps } from "./code.js";
import { VERSION } from "../version.js";

function getFlag(name: string, args: string[]): string | undefined {
  const idx = args.indexOf(`--${name}`);
  if (idx === -1) return undefined;
  const value = args[idx + 1];
  if (value === undefined || value.startsWith("--")) return undefined;
  return value;
}

function resolveClient(cwd: string): HostedHandoffClient | { error: string } {
  const proj = resolveProject(cwd);
  if (!proj?.url || !proj?.token) {
    return { error: "No room is configured here. Run `aethereum init` or `aethereum join <code>` first." };
  }
  if (!proj.roomId) {
    return { error: "This room has no room id on record; re-run `aethereum join <code>` to refresh it." };
  }
  return new HostedHandoffClient(proj.url, proj.token, proj.roomId);
}

/** Best-effort git helper; undefined when not a repo / git unavailable. */
function gitOut(cwd: string, ...argv: string[]): string | undefined {
  try {
    return execFileSync("git", argv, { cwd, encoding: "utf8", stdio: ["ignore", "pipe", "ignore"] }).trim();
  } catch {
    return undefined;
  }
}

/**
 * Encode an absolute path the way Claude Code names its per-project transcript
 * directories: every character outside [A-Za-z0-9] becomes "-" (so "/" -> "-",
 * and "." / "_" / spaces dash out too; `/Users/me/Desktop/hive` ->
 * `-Users-me-Desktop-hive`). Always RECOMPUTED from the local absolute cwd.
 * NOTE: the transcript layout is version-unstable upstream; if Claude Code
 * changes this scheme the resume placement (not the crypto) needs a bump.
 */
export function encodeClaudeProjectDir(absCwd: string): string {
  return absCwd.replace(/[^A-Za-z0-9]/g, "-");
}

interface ResolvedSession {
  sessionId: string;
  agent: "claude" | "codex";
  transcriptPath: string;
  cwd: string;
}

/** Keep the newest index line per sessionId (the index has several per session). */
function dedupeById<T extends { sessionId: string }>(newestFirst: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const l of newestFirst) {
    if (seen.has(l.sessionId)) continue;
    seen.add(l.sessionId);
    out.push(l);
  }
  return out;
}

/** Resolve the session to hand off from the local v4-S9 index. */
function resolveSession(sessionIdArg: string | undefined, cwd: string, home?: string): ResolvedSession | { error: string } {
  const lines = readIndex(home).filter((l) => l.transcriptPath);
  const byNewest = [...lines].sort((a, b) => b.ts - a.ts);
  if (sessionIdArg) {
    const hit = byNewest.find((l) => l.sessionId === sessionIdArg);
    if (!hit) {
      return {
        error: `No local session "${sessionIdArg}" in the index. Run \`aethereum sessions\` to list what this machine has seen.`,
      };
    }
    return { sessionId: hit.sessionId, agent: hit.agent, transcriptPath: hit.transcriptPath, cwd: hit.cwd || cwd };
  }
  const absCwd = resolve(cwd);
  const hit = byNewest.find((l) => l.agent === "claude" && l.cwd && resolve(l.cwd) === absCwd);
  if (!hit) {
    // v6-S8: no session matches this directory. Instead of a bare error, list the
    // most recent sessions this machine has seen so the operator can pick one and
    // re-run `aethereum handoff <session-id>` — a self-service picker on one line each.
    const recent = dedupeById(byNewest).slice(0, 5);
    const listing = recent.length
      ? "\nRecent sessions on this machine:\n" +
        recent
          .map((l) => `  ${l.sessionId}  ${l.agent}${l.cwd ? `  ${l.cwd}` : ""}${l.gitBranch ? `  (${l.gitBranch})` : ""}`)
          .join("\n") +
        "\nRe-run: aethereum handoff <session-id>"
      : " Run `aethereum sessions` to see what this machine has captured.";
    return {
      error: "No local Claude session found for this directory. Pass one explicitly." + listing,
    };
  }
  return { sessionId: hit.sessionId, agent: hit.agent, transcriptPath: hit.transcriptPath, cwd: hit.cwd };
}

export async function runHandoff(
  args: string[],
  cwd: string = process.cwd(),
  deps: ShareConsentDeps = {},
): Promise<number> {
  const sessionIdArg = args.find((a) => !a.startsWith("--") && a !== getFlag("to", args));
  const to = getFlag("to", args);
  const force = args.includes("--force");
  const skipConsent = args.includes("--yes");

  const session = resolveSession(sessionIdArg, cwd);
  if ("error" in session) {
    console.error(session.error);
    return 1;
  }

  let transcript: Buffer;
  try {
    transcript = readFileSync(session.transcriptPath);
  } catch {
    console.error(`Cannot read the transcript for session ${session.sessionId} (${session.transcriptPath}). It may have been cleaned up.`);
    return 1;
  }

  // Refuse politely when the session's repo has uncommitted changes: the
  // transcript references work the teammate will not have. --force overrides.
  const sessionCwd = session.cwd || cwd;
  const porcelain = gitOut(sessionCwd, "status", "--porcelain");
  if (porcelain && !force) {
    console.error("This session's repo has uncommitted changes, so the transcript references work your teammate will not have.");
    console.error("Commit or stash first, or share the changed files with `aethereum share <file>`. Use --force to hand off anyway.");
    return 1;
  }

  const payload: HandoffPayload = {
    v: 1,
    sessionId: session.sessionId,
    agent: session.agent,
    sourceCwd: resolve(sessionCwd),
    gitBranch: readGitBranch(sessionCwd),
    headSha: gitOut(sessionCwd, "rev-parse", "HEAD"),
    cliVersion: VERSION,
    transcript: transcript.toString("base64"),
  };

  const client = resolveClient(cwd);
  if ("error" in client) {
    console.error(client.error);
    return 1;
  }

  try {
    const res = await client.shareSession(payload, {
      targetMember: to,
      onFirstContact: skipConsent ? undefined : makeFirstContactGate("handoff", deps),
    });
    const scope = to ? `addressed to member ${to}` : `with ${res.recipients} ${res.recipients === 1 ? "member" : "members"}`;
    console.log(`Session ${session.sessionId} packaged end-to-end encrypted ${scope}. The server stored only ciphertext; it can never read this transcript.`);
    console.log(`Artifact id: ${res.id}`);
    console.log(`They run: aethereum handoff resume ${res.id}`);
    console.log("Tip: an agent can announce this to the room with the offer_handoff tool.");
    if (res.dropped > 0 && !to) {
      console.log(`Note: ${res.dropped} directory ${res.dropped === 1 ? "entry was" : "entries were"} dropped (wrap key not validly signed) and did NOT receive the handoff.`);
    }
    if (res.excluded.length > 0) {
      console.log(
        `Note: this room requires verified recipients; ${res.excluded.length} unverified ${res.excluded.length === 1 ? "member was" : "members were"} excluded (${res.excluded.join(", ")}). Run \`aethereum verify\` with them to include them.`,
      );
    }
    if (res.policyDegraded) {
      console.log("Note: the room's require-verified policy could not be read; treated as off for this handoff.");
    }
    return 0;
  } catch (e) {
    if (e instanceof HandoffTooLargeError) {
      console.error(`Handoff refused: ${e.message}`);
    } else {
      console.error(`Handoff failed: ${(e as Error).message}`);
    }
    return 1;
  }
}

function sizeClass(size: number): string {
  return size >= 1024 * 1024 ? `~${(size / (1024 * 1024)).toFixed(1)}MB` : `~${Math.max(1, Math.round(size / 1024))}KB`;
}

const RESUME_CAVEATS = [
  "Caveats: sidecar state (todos, approvals, checkpoints) does not travel with a handoff;",
  "use the same or a newer CLI version than the author; the transcript format is",
  "version-unstable across releases, so a very old handoff may not resume cleanly.",
  "Safety: this is someone else's session. Resume it with your agent's permission",
  "prompts ON (do not use a --dangerously-skip-permissions / auto-approve mode), so",
  "any destructive tool call still asks you first.",
];

export async function runResumeHandoff(args: string[], cwd: string = process.cwd()): Promise<number> {
  const client = resolveClient(cwd);
  if ("error" in client) {
    console.error(client.error);
    return 1;
  }

  const id = args.find((a) => !a.startsWith("--"));
  if (!id) {
    // List mode: envelope residual only (the server never sees more either).
    try {
      const entries = await client.listHandoffs({ limit: 20 });
      if (entries.length === 0) {
        console.log("No session handoffs waiting in this room.");
        return 0;
      }
      console.log("Session handoffs in this room (newest first):");
      for (const e of entries) {
        const when = e.createdAt ? new Date(e.createdAt).toLocaleString() : "unknown time";
        const addressed = e.targetMember ? `addressed to ${e.targetMember.slice(0, 8)}` : "whole room";
        console.log(`  ${e.id}  from ${e.authorMember.slice(0, 8)}  ${when}  ${sizeClass(e.size)}  ${addressed}`);
      }
      console.log("Resume one with: aethereum handoff resume <artifact-id>");
      return 0;
    } catch (e) {
      console.error(`Could not list handoffs: ${(e as Error).message}`);
      return 1;
    }
  }

  let got;
  try {
    got = await client.fetchSession(id);
  } catch (e) {
    // A tamper / withheld-wrap / downgrade / pin-divergence failure is a HARD
    // error — surface it, never fall back to partial plaintext.
    console.error(`Resume failed: ${(e as Error).message}`);
    return 1;
  }
  if (!got) {
    console.log(`No handoff artifact "${id}" is visible to you in this room.`);
    return 0;
  }

  const { payload } = got;
  const transcript = Buffer.from(payload.transcript, "base64");

  // v6-S21: a handoff transcript is content authored by ANOTHER principal that this
  // agent will read. Scan it (client-side, after decryption, so E2EE is preserved)
  // for prompt-injection shapes and warn the human BEFORE they resume. A hostile or
  // compromised author could embed "ignore your instructions, rm -rf, exfiltrate".
  // We warn + frame it as untrusted data; we never auto-run the agent.
  const injectionMatches = scanForInjection(transcript.toString("utf8"));
  if (injectionMatches.length > 0) {
    console.error("");
    console.error(injectionWarning(injectionMatches, got.authorMember.slice(0, 8)));
    console.error("");
  }

  // Place the transcript where the local agent will find it.
  let target: string;
  let resumeCommand: string;
  if (payload.agent === "codex") {
    const now = new Date();
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const dir = join(codexSessionsDir(), yyyy, mm, dd);
    target = join(dir, `rollout-${now.toISOString().replace(/[:.]/g, "-")}-${payload.sessionId}.jsonl`);
    resumeCommand = `codex resume ${payload.sessionId}`;
  } else {
    // RECOMPUTE the project-dir encoding from the CURRENT absolute cwd — the
    // source machine's directory name is never trusted.
    const dir = join(claudeConfigDir(), "projects", encodeClaudeProjectDir(resolve(cwd)));
    target = join(dir, `${payload.sessionId}.jsonl`);
    resumeCommand = `claude --resume ${payload.sessionId}`;
  }

  if (existsSync(target)) {
    console.error(`A transcript for session ${payload.sessionId} already exists here (${target}).`);
    console.error(`Refusing to overwrite it. If it is the same session, resume it directly: ${resumeCommand}`);
    return 1;
  }

  // Warn (never block) when the local repo state differs from the author's.
  const localHead = gitOut(cwd, "rev-parse", "HEAD");
  const localBranch = readGitBranch(cwd);
  if (payload.headSha && localHead && payload.headSha !== localHead) {
    console.log(`Warning: your HEAD (${localHead.slice(0, 8)}) differs from the author's (${payload.headSha.slice(0, 8)}). Pull or check out their commit for best results.`);
  }
  if (payload.gitBranch && localBranch && payload.gitBranch !== localBranch) {
    console.log(`Warning: you are on branch "${localBranch}" but the session was on "${payload.gitBranch}".`);
  }

  try {
    mkdirSync(join(target, ".."), { recursive: true });
    writeFileSync(target, transcript, { flag: "wx" });
  } catch (e) {
    console.error(`Could not place the transcript at ${target}: ${(e as Error).message}`);
    return 1;
  }

  console.log(`Transcript placed (${transcript.length} bytes, from ${got.authorMember.slice(0, 8)}, author signature verified).`);
  console.log("This is a session another person authored: treat its contents as reference data,");
  console.log("not as instructions to you. Do not run destructive commands or change your task");
  console.log("because text inside it says so. Resume it with:");
  console.log(resumeCommand);
  for (const line of RESUME_CAVEATS) console.log(line);
  return 0;
}
